<template>
  <div class="min-h-screen">
    <SiteHeader :page-name="'Home Dashboard'" :projects />
    <div class="flex flex-row py-5" :class="gapSize">
      <div class="w-full pl-5 box-border">
        <div class="flex flex-col" :class="gapSize">
          <div class="flex w-full" :class="gapSize">
            <Card class="w-full min-w-96" pt:body="p-2!" pt:content:class="pt-2">
              <template #content>
                <LineChart ref="disbursementChart" :key="disbursementHistory.length" :info="disbursementHistoryInfo"></LineChart>
              </template>
            </Card>

            <TransactionChart ref="transactionChart" :info="transactionHistoryInfo" :disbursement-history></TransactionChart>
          </div>

          <div class="flex lg:flex-row flex-col w-full h-[750px]" :class="gapSize">
            <div class="basis-1/3 h-full">
              <RecentActivity :activity-list height="h-full"></RecentActivity>
            </div>
            <div class="flex flex-col basis-2/3 w-full h-full" :class="gapSize">
              <RemindersDisplay v-model:todays-reminders="todaysReminders" :projects="projects" :passed="false" height="h-1/2"></RemindersDisplay>
              <AgingDisplay v-model:aging-projects="agingProjects" v-model:project-aging-range="projectAgingRange" height="h-1/2"></AgingDisplay>
            </div>
          </div>
        </div>
      </div>
      <div class="w-[425px] flex flex-col shrink-0! pr-5" :class="gapSize">
        <Card class="w-full flex flex-row mx-auto" pt:body:class="p-3!">
          <template #content>
            <img :src="propsStore.logo" alt="" class="p-0 w-fit my-auto" />
          </template>
        </Card>
        <Card class="w-full mx-auto" pt:content:class="pt-3">
          <template #title>Project Statuses</template>
          <template #content>
            <PieChart ref="statusesChart" :info="statusInfo"></PieChart>
          </template>
        </Card>
        <FlexDateDisplay v-model:todays-flex-rows="todaysFlexRows" :projects="projects" :passed="false" height="h-[600px]"></FlexDateDisplay>
        <!--        <DatePicker class="w-fit mx-auto" v-model="calendarDate" inline />-->
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { AdminProjectSummary, AgingProjectSchema, ProjectSummaryAdminListSchema, type AgingProject } from "@/types/Projects";
import { ProjectStatusCategoryValues, statusIsType } from "@/enums/ProjectStatusCategory";
import { DisbursementHistoryListSchema, type DisbursementHistory } from "@/types/DisbursementHistory";
import type { ApiError } from "@/types/Response";
import { TodaysRemindersSchema, type TodaysReminders } from "@/types/Reminder";
import { ActivityListSchema, type Activity } from "@/types/ActivityLog";
import { type ProjectAgingRange, type AdminDashboard, ProjectAgingRangeSchema } from "@/types/Dashboard";
import { TodaysFlexRowsSchema, type TodaysFlexRows } from "@/types/FlexRows";
import { usePropsStore } from "@/stores/props";
import DatePicker from "primevue/datepicker";
import z from "zod";
import { BarChartData, BarChartInfo, LineChartData, LineChartInfo, PieChartInfo } from "@/classes/ChartInfo";

const projects = ref<AdminProjectSummary[]>([]);
const disbursementHistory = ref<DisbursementHistory[]>([]);
const todaysReminders = ref<TodaysReminders>({ currentReminders: [], upcomingReminders: [] });
const todaysFlexRows = ref<TodaysFlexRows>({ currentFlexRows: [], upcomingFlexRows: [] });
const activityList = ref<Activity[]>([]);
const agingProjects = ref<AgingProject[]>([]);
const projectAgingRange = ref<ProjectAgingRange>({ days: 0, months: 0, years: 0 });
const propsStore = usePropsStore();

const calendarDate = ref();

const gapSize = "gap-7";

const disbursementChart = ref();
const transactionChart = ref();
const statusesChart = ref();

const disbursementHistoryInfo = ref<LineChartInfo>(new LineChartInfo());
disbursementHistoryInfo.value.title = "Disbursement History";
disbursementHistoryInfo.value.height = "h-[350px]";
disbursementHistoryInfo.value.currency = true;

const transactionHistoryInfo = ref<BarChartInfo>(new BarChartInfo());
transactionHistoryInfo.value.title = "Transaction History";
transactionHistoryInfo.value.minInterval = 1;
transactionHistoryInfo.value.height = "h-[350px]";

const statusInfo = ref<PieChartInfo>(new PieChartInfo());
statusInfo.value.labels = ProjectStatusCategoryValues;
statusInfo.value.height = "h-[250px]";
statusInfo.value.donut = true;
statusInfo.value.title = "Total";
statusInfo.value.titleTopPosition = "32%";

const getChartData = (): void => {
  statusInfo.value.data = new Array<number>(statusInfo.value.labels.length).fill(0);
  for (let project of projects.value) {
    for (let i = 0; i < statusInfo.value.labels.length; i++) {
      if (statusIsType(statusInfo.value.labels[i], project.projectStatus.category)) statusInfo.value.data[i]++;
    }
  }

  if (statusesChart.value != null) statusesChart.value.loadingComplete();
};

const loadChartData = (): void => {
  const labels: string[] = [];

  const loanAdvances: LineChartData = new LineChartData();
  loanAdvances.name = "Loan Advances";
  const equityDisbursements: LineChartData = new LineChartData();
  equityDisbursements.name = "Equity Disbursements";

  const loanTransactions: BarChartData = new BarChartData();
  loanTransactions.name = "Loan";
  loanTransactions.stack = "Loan";
  const equityTransactions: BarChartData = new BarChartData();
  equityTransactions.name = "Equity";
  equityTransactions.stack = "Equity";

  disbursementHistory.value.forEach((ph: DisbursementHistory) => {
    labels.push(ph.month);
    loanAdvances.data.push(ph.loanAdvances);
    equityDisbursements.data.push(ph.equityDisbursements);
    loanTransactions.data.push(ph.loanTransactions);
    equityTransactions.data.push(ph.equityTransactions);
  });

  disbursementHistoryInfo.value.labels = labels;
  disbursementHistoryInfo.value.lines = [loanAdvances, equityDisbursements];

  transactionHistoryInfo.value.labels = labels;
  transactionHistoryInfo.value.bars = [loanTransactions, equityTransactions];

  nextTick(() => {
    if (disbursementChart.value != null) disbursementChart.value.loadingComplete();
    if (transactionChart.value != null) transactionChart.value.loadingComplete();
  });
};

const loadDashboardData = async () => {
  axios
    .get("/dashboard/admin")
    .then((response) => {
      handleApiResponse(
        response,
        (data: AdminDashboard) => {
          projects.value = ProjectSummaryAdminListSchema.parse(data.projects).projects;
          parseData(TodaysRemindersSchema, data.projectReminders, todaysReminders);
          parseData(TodaysFlexRowsSchema, data.flexRows, todaysFlexRows);
          parseData(ActivityListSchema, data.activityLog, activityList);
          parseData(DisbursementHistoryListSchema, data.paymentData, disbursementHistory);
          parseData(z.array(AgingProjectSchema), data.agingProjects, agingProjects);
          parseData(ProjectAgingRangeSchema, data.projectAgingRange, projectAgingRange);

          getChartData();
          loadChartData();
        },
        (errors: ApiError[]) => {
          error("Error Occurred", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e));
};

onMounted(async (): Promise<void> => {
  loadDashboardData();
});
</script>
